import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.01 3.2c-7.06 0-12.8 5.73-12.8 12.79 0 2.26.6 4.47 1.73 6.41L3.2 28.8l6.57-1.72a12.76 12.76 0 0 0 6.24 1.59h.01c7.05 0 12.79-5.73 12.79-12.79 0-3.42-1.33-6.63-3.75-9.05a12.7 12.7 0 0 0-9.05-3.63zm0 23.42h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-4.03 1.06 1.07-3.93-.25-.4a10.6 10.6 0 0 1-1.62-5.65c0-5.86 4.77-10.63 10.64-10.63 2.84 0 5.51 1.11 7.52 3.12a10.56 10.56 0 0 1 3.11 7.52c0 5.87-4.77 10.62-10.64 10.62zm5.83-7.96c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1.01 1.25-.18.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.71-.97-2.34-.26-.62-.52-.54-.71-.55l-.6-.01c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63s1.13 3.05 1.29 3.26c.16.21 2.22 3.39 5.38 4.76.75.32 1.34.51 1.8.66.76.24 1.44.21 1.99.13.61-.09 1.89-.77 2.15-1.52.27-.74.27-1.38.19-1.51-.08-.13-.29-.21-.61-.37z" />
  </svg>
);

// Ultimate fallback so the button always renders, even if production settings
// have no WhatsApp number configured. Admin settings override this value.
const DEFAULT_WHATSAPP = "919337150062";

export const WhatsAppButton = () => {
  const { whatsapp, social } = useSettings();
  const [open, setOpen] = useState(false);
  const invalid = (n) => !n || /0{5,}/.test(n) || n.length < 10;
  let number = (whatsapp || "").replace(/[^\d]/g, "");
  // Fall back to the configured WhatsApp social link if the number field is still a placeholder
  if (invalid(number) && social?.whatsapp) {
    const m = (social.whatsapp.match(/\d{8,}/) || [])[0];
    if (m) number = m;
  }
  // Final fallback to the business number so the widget shows everywhere
  if (invalid(number)) number = DEFAULT_WHATSAPP;
  const href = `https://wa.me/${number}?text=${encodeURIComponent("Hi Nutribest! I have a question about your superfoods.")}`;

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3" data-testid="whatsapp-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.94 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="w-[268px] overflow-hidden rounded-2xl bg-[#F7F4EC] shadow-2xl ring-1 ring-[#13281C]/10"
            data-testid="whatsapp-card"
          >
            <div className="flex items-center justify-between bg-[#13281C] px-4 py-3">
              <div className="flex items-center gap-2 text-[#F7F4EC]">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-[#25D366]">
                  <WhatsAppIcon className="h-5 w-5 text-white" />
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-semibold">Nutribest</p>
                  <p className="text-[11px] text-[#B08D3E]">Typically replies in minutes</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#F7F4EC]/70 transition hover:text-white" data-testid="whatsapp-close-btn" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="rounded-lg rounded-tl-none bg-white p-3 text-sm text-[#13281C] shadow-sm">
                Hi there! Questions about our superfoods, an order, or partnerships? Chat with us on WhatsApp.
              </div>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-2.5 text-sm font-semibold text-white transition hover:bg-[#1eb955]"
                data-testid="whatsapp-start-chat-btn"
              >
                <WhatsAppIcon className="h-4 w-4" />
                Start chat
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((v) => !v)}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="relative grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#2ee06a] to-[#128C7E] text-white shadow-[0_8px_24px_-4px_rgba(37,211,102,0.6),0_4px_8px_-2px_rgba(0,0,0,0.2)] ring-4 ring-white/70"
        data-testid="whatsapp-toggle-btn"
        aria-label="Chat on WhatsApp"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="h-6 w-6" strokeWidth={2.5} />
            </motion.span>
          ) : (
            <motion.span key="wa" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <WhatsAppIcon className="h-8 w-8 drop-shadow-sm" />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#25D366] opacity-30" />}
      </motion.button>
    </div>
  );
};
